import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { AdminMenuCounts, AssignMailInput, UnassignedMail } from '@nest-recruit/shared';
import { ApiError, errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { ADMIN_MENU_COUNTS_KEY } from '../../../admin/api/overview-api';
import { INBOX_KEY, TIMELINE_KEY, communicationApi } from '../../api/communication-api';

export interface InboxActions {
  /** Filed or dismissed, the answer not back yet: the list leaves these out meanwhile. */
  readonly leaving: ReadonlySet<string>;
  /** Put back by Undo, the list not read again yet: shown meanwhile. */
  readonly returning: readonly UnassignedMail[];
  readonly file: (mail: UnassignedMail, target: AssignMailInput, label: string) => void;
  readonly dismiss: (mail: UnassignedMail) => void;
}

/** The page's list as it stands after the clicks still on their way. Newest first, as the server sends it. */
export function shownMail(mail: readonly UnassignedMail[], actions: Pick<InboxActions, 'leaving' | 'returning'>): readonly UnassignedMail[] {
  const ids = new Set(mail.map((each) => each.messageId));
  return [...mail, ...actions.returning.filter((each) => !ids.has(each.messageId))]
    .filter((each) => !actions.leaving.has(each.messageId))
    .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
}

/**
 * Filing and Dismiss on Inbound mail (Admin redesign batch 2), the instant-feel way (ADR 0002): the email leaves the
 * list and the red count drops on the click; the server answers after. A refusal brings it back and says why, except
 * "someone has just filed it", where it really has gone. Dismiss has a 10-second Undo from the moment of the click;
 * an Undo pressed before the server has answered waits for that answer, then puts the email back. Filing has no Undo:
 * the server keeps no way to take an email off a timeline.
 */
export function useInboxActions(onRestored: (id: string) => void): InboxActions {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const closeToast = useToasts((state) => state.dismiss);
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const [returning, setReturning] = useState<readonly UnassignedMail[]>([]);

  const setIsLeaving = (id: string, isLeaving: boolean): void => {
    setLeaving((current) => {
      const next = new Set(current);
      if (isLeaving) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const setIsReturning = (mail: UnassignedMail, isReturning: boolean): void => {
    setReturning((current) => [...current.filter((each) => each.messageId !== mail.messageId), ...(isReturning ? [mail] : [])]);
  };
  const moveCount = (by: number): void => {
    queryClient.setQueryData<AdminMenuCounts>(ADMIN_MENU_COUNTS_KEY, (counts) => (counts && counts.inboxWaiting !== null ? { ...counts, inboxWaiting: Math.max(0, counts.inboxWaiting + by) } : counts));
  };
  /** Held until the list has been read again, so the email never blinks back in or out (06-qol §5). */
  const settle = async (alsoTimeline: boolean): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: INBOX_KEY }),
      queryClient.invalidateQueries({ queryKey: ADMIN_MENU_COUNTS_KEY }),
      ...(alsoTimeline ? [queryClient.invalidateQueries({ queryKey: TIMELINE_KEY })] : []),
    ]);
  };
  const isGoneAnyway = (cause: unknown): boolean => cause instanceof ApiError && (cause.status === 409 || cause.status === 404);

  const file = (mail: UnassignedMail, target: AssignMailInput, label: string): void => {
    setIsLeaving(mail.messageId, true);
    moveCount(-1);
    communicationApi
      .assignMail(mail.messageId, target)
      .then(
        () => {
          show({ tone: 'success', message: `Filed against ${label}`, detail: 'It is on their timeline now.' });
          return settle(true);
        },
        (cause: unknown) => {
          if (!isGoneAnyway(cause)) moveCount(1);
          show({ tone: 'danger', message: "It wasn't filed", detail: errorMessage(cause) });
          return settle(false);
        },
      )
      .finally(() => setIsLeaving(mail.messageId, false));
  };

  const restore = (mail: UnassignedMail): void => {
    setIsReturning(mail, true);
    setIsLeaving(mail.messageId, false);
    moveCount(1);
    onRestored(mail.messageId);
    communicationApi
      .restoreMail(mail.messageId)
      .then(
        () => settle(false),
        (cause: unknown) => {
          moveCount(-1);
          show({ tone: 'danger', message: "It wasn't put back", detail: errorMessage(cause) });
          return settle(false);
        },
      )
      .finally(() => setIsReturning(mail, false));
  };

  const dismiss = (mail: UnassignedMail): void => {
    setIsLeaving(mail.messageId, true);
    moveCount(-1);
    const request = communicationApi.dismissMail(mail.messageId);
    const toastId = showUndo(`Dismissed “${mail.subject}”`, () => {
      request.then(
        () => restore(mail),
        () => undefined,
      );
    });
    request
      .then(
        () => settle(false),
        (cause: unknown) => {
          closeToast(toastId);
          if (!isGoneAnyway(cause)) moveCount(1);
          show({ tone: 'danger', message: "It wasn't dismissed", detail: errorMessage(cause) });
          return settle(false);
        },
      )
      .finally(() => setIsLeaving(mail.messageId, false));
  };

  return { leaving, returning, file, dismiss };
}
